import { globalVFS } from '@/OS/utils/storage/VFS';
import {
  dispatchAgentBlocked,
  isAgentGuardEnabled,
  isAgentOperationAllowed,
  normalizeWinPath,
} from '@/lib/agentAccess';

export type GuardedVFSOp = 'load' | 'save' | 'delete' | 'list' | 'exists' | 'createDirectory';

function block(op: GuardedVFSOp, path: string) {
  const p = normalizeWinPath(path);
  dispatchAgentBlocked({ op, path: p, reason: 'Path not in agent allowlist' });
  throw new Error(`Agent access blocked for ${op}: ${p}`);
}

export type VFSLike = {
  save(path: string, content: string): Promise<void>;
  load(path: string): Promise<string | null>;
  delete(path: string): Promise<void>;
  list(path: string): Promise<string[]>;
  exists(path: string): Promise<boolean>;
  createDirectory(path: string): Promise<void>;
};

export function createGuardedVFS(base: VFSLike = globalVFS): VFSLike {
  return {
    async save(path: string, content: string) {
      if (isAgentGuardEnabled() && !isAgentOperationAllowed(path)) block('save', path);
      return base.save(path, content);
    },
    async load(path: string) {
      if (isAgentGuardEnabled() && !isAgentOperationAllowed(path)) block('load', path);
      return base.load(path);
    },
    async delete(path: string) {
      if (isAgentGuardEnabled() && !isAgentOperationAllowed(path)) block('delete', path);
      return base.delete(path);
    },
    async list(path: string) {
      if (isAgentGuardEnabled() && !isAgentOperationAllowed(path)) block('list', path);
      return base.list(path);
    },
    async exists(path: string) {
      if (isAgentGuardEnabled() && !isAgentOperationAllowed(path)) block('exists', path);
      return base.exists(path);
    },
    async createDirectory(path: string) {
      if (isAgentGuardEnabled() && !isAgentOperationAllowed(path)) block('createDirectory', path);
      return base.createDirectory(path);
    },
  };
}

const guarded = createGuardedVFS(globalVFS);

/**
 * Use this in agent-marked flows.
 * When guard is disabled, it behaves like the normal VFS.
 */
export function getGuardedVFS(): VFSLike {
  return isAgentGuardEnabled() ? guarded : globalVFS;
}
